// frontend/packages/business-console/src/contexts/SnackbarContext.jsx
//
// Lightweight toast notification system built on MUI Snackbar + Alert.
//
// Usage in any component:
//   const { showSnackbar } = useSnackbar();
//   showSnackbar("Profile saved successfully", "success");
//   showSnackbar("Something went wrong", "error");
//
// Severity values: "success" | "error" | "warning" | "info"
//
// Only one snackbar is shown at a time. Calling showSnackbar while one
// is visible will replace it immediately.

import React, { createContext, useCallback, useContext, useState } from "react";
import { Alert, Snackbar } from "@mui/material";

const SnackbarContext = createContext({
  showSnackbar: () => {},
});

/** Auto-dismiss duration in milliseconds. */
const AUTO_HIDE_MS = 5000;

export function SnackbarProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");

  const showSnackbar = useCallback((msg, sev = "info") => {
    setMessage(String(msg || ""));
    setSeverity(sev);
    setOpen(true);
  }, []);

  const handleClose = useCallback((_event, reason) => {
    // Don't close on clickaway — user must wait for timeout or click X
    if (reason === "clickaway") return;
    setOpen(false);
  }, []);

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}

      <Snackbar
        open={open}
        autoHideDuration={AUTO_HIDE_MS}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpen(false)}
          severity={severity}
          variant="filled"
          sx={{ width: "100%", fontWeight: 600 }}
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

export const useSnackbar = () => useContext(SnackbarContext);
